import { supabase } from '@/lib/supabase';

/**
 * Platform-admin helpers for the cockpit (0075 RPCs).
 * All functions error if the caller is not is_platform_admin.
 */

export interface AdminDashboardKpis {
  total_orgs: number;
  trial_count: number;
  pilot_count: number;
  active_count: number;
  past_due_count: number;
  canceled_count: number;
  trials_ending_7d: number;
  signups_7d: number;
  mrr_estimated_cad: number;
}

export interface AdminSubscriptionRow {
  organization_id: string;
  org_display_name: string;
  primary_contact_email: string | null;
  org_created_at: string;
  status: string | null;
  tier: string | null;
  billing_period: string | null;
  trial_ends_at: string | null;
  pilot_ends_at: string | null;
  current_period_end: string | null;
  internal_note: string | null;
  active_location_count: number;
  staff_count: number;
  monthly_rate_cad: number;
  mrr_cad: number;
  updated_at: string | null;
  // Discount fields (0079)
  discount_pct: number | null;
  discount_amount_off_cad: number | null;
  discount_ends_at: string | null;
  discount_reason: string | null;
  applied_coupon_id: string | null;
  applied_coupon_code: string | null;
  effective_monthly_rate_cad: number;
}

export interface AdminOrgRow {
  organization_id: string;
  display_name: string;
  legal_name: string;
  primary_contact_email: string | null;
  timezone: string;
  org_status: string;
  org_created_at: string;
  location_count: number;
  active_location_count: number;
  staff_count: number;
  sub_status: string | null;
  sub_tier: string | null;
  billing_period: string | null;
  mrr_cad: number;
  // 0106: legal-entity address (org-level, separate from per-pharmacy locations)
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  phone: string | null;
  // 0106: primary owner contact
  owner_user_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_phone: string | null;
}

export interface AdminRecentSignup {
  organization_id: string;
  display_name: string;
  primary_contact_email: string | null;
  org_created_at: string;
  status: string | null;
}

export interface AdminAuditEvent {
  event_id: string;
  organization_id: string | null;
  org_display_name: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  event_type: string;
  summary: string;
  target_type: string | null;
  target_id: string | null;
  occurred_at: string;
}

export type AdminPilotHealthLevel = 'green' | 'amber' | 'red' | 'no_plan';

export interface AdminPilotHealthRow {
  organization_id: string;
  display_name: string;
  primary_contact_email: string | null;
  status: string | null;
  tier: string | null;
  trial_ends_at: string | null;
  active_location_count: number;
  active_staff_count: number;
  last_owner_signin_at: string | null;
  last_schedule_published_at: string | null;
  org_created_at: string;
  days_since_signup: number;
  health: AdminPilotHealthLevel;
  reason: string;
}

// ------ reads -----

export async function getDashboardKpis(): Promise<AdminDashboardKpis> {
  const { data, error } = await supabase.rpc('admin_dashboard_kpis');
  if (error) throw error;
  return data as AdminDashboardKpis;
}

export async function listAllSubscriptions(): Promise<AdminSubscriptionRow[]> {
  const { data, error } = await supabase.rpc('admin_subscriptions_list');
  if (error) throw error;
  return (data ?? []) as AdminSubscriptionRow[];
}

export async function listAllOrgs(): Promise<AdminOrgRow[]> {
  const { data, error } = await supabase.rpc('admin_orgs_with_metrics');
  if (error) throw error;
  return (data ?? []) as AdminOrgRow[];
}

export async function listRecentSignups(limit = 10): Promise<AdminRecentSignup[]> {
  const { data, error } = await supabase.rpc('admin_recent_signups', { p_limit: limit });
  if (error) throw error;
  return (data ?? []) as AdminRecentSignup[];
}

export async function listRecentAuditEvents(limit = 20): Promise<AdminAuditEvent[]> {
  const { data, error } = await supabase.rpc('admin_recent_audit_events', { p_limit: limit });
  if (error) throw error;
  return (data ?? []) as AdminAuditEvent[];
}

export async function getPilotHealth(): Promise<AdminPilotHealthRow[]> {
  const { data, error } = await supabase.rpc('admin_pilot_health');
  if (error) throw error;
  return (data ?? []) as AdminPilotHealthRow[];
}

// ------ writes -----

export async function adminExtendTrial(orgId: string, additionalDays: number, reason: string | null) {
  const { error } = await supabase.rpc('admin_extend_trial', {
    p_org_id: orgId, p_additional_days: additionalDays, p_reason: reason,
  });
  if (error) throw error;
}

export async function adminSetPilot(orgId: string, days: number, reason: string | null) {
  const { error } = await supabase.rpc('admin_set_pilot', {
    p_org_id: orgId, p_days: days, p_reason: reason,
  });
  if (error) throw error;
}

export async function adminCompActive(
  orgId: string,
  tier: 'standard' | 'pro' | 'enterprise',
  billingPeriod: 'monthly' | 'annual',
  periodEndIso: string,
  reason: string | null,
) {
  const { error } = await supabase.rpc('admin_comp_active', {
    p_org_id: orgId, p_tier: tier, p_billing_period: billingPeriod,
    p_period_end: periodEndIso, p_reason: reason,
  });
  if (error) throw error;
}

export async function adminMarkPastDue(orgId: string, reason: string | null) {
  const { error } = await supabase.rpc('admin_mark_past_due', { p_org_id: orgId, p_reason: reason });
  if (error) throw error;
}

export async function adminCancelSubscription(orgId: string, reason: string | null) {
  const { error } = await supabase.rpc('admin_cancel_subscription', { p_org_id: orgId, p_reason: reason });
  if (error) throw error;
}

export async function adminSetInternalNote(orgId: string, note: string) {
  const { error } = await supabase.rpc('admin_set_internal_note', { p_org_id: orgId, p_note: note });
  if (error) throw error;
}

// ------ formatting -----

export function formatCAD(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
  }).format(amount);
}

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
}
